import type { ComponentPropsWithRef } from 'react'
import { forwardRef } from 'react'

import type { UseComboboxGetItemPropsOptions } from 'downshift'
import { isEqual } from 'lodash'

import type { SelectOption } from '../../CommonProps'
import { Anchor } from './Anchor'
import { Container } from './Container'
import { Empty } from './Empty'
import { Item } from './Item'
import { ItemContent } from './ItemContent'
import { Label } from './Label'

type Props = ComponentPropsWithRef<'div'> & {
	highlightedOptionIndex: number
	isOpen: boolean
	optionsMaxVisible: number
	options: Array<SelectOption>
	selectedItems: Array<SelectOption>

	getItemProps: (options: UseComboboxGetItemPropsOptions<SelectOption>) => any
	onSelectOption?: (item: SelectOption) => void
}

export const SelectMenu = forwardRef<HTMLDivElement, Props>(
	(
		{ getItemProps, highlightedOptionIndex, isOpen, optionsMaxVisible, onSelectOption, options, selectedItems },
		ref,
	) => (
		<Anchor>
			<Container ref={ref} isOpen={isOpen}>
				{options.length === 0 ? (
					<Empty>Nothing matches your search</Empty>
				) : (
					options.slice(0, optionsMaxVisible).map((item, index: number) => (
						<Item
							{...(getItemProps({
								key: `${item.label}-${index}`,
								index,
								item,
								onClick: onSelectOption && !item.isDisabled ? () => onSelectOption(item) : undefined,
							}) as Record<string, unknown>)}
							key={index}
							isDisabled={item.isDisabled ?? false}
						>
							<ItemContent
								isHighlighted={highlightedOptionIndex === index}
								isSelected={selectedItems.some((selectedItem) => isEqual(selectedItem, item))}
								isDisabled={item.isDisabled ?? false}
							>
								{item.icon?.({ size: 22 })}
								<Label>{item.label}</Label>
							</ItemContent>
						</Item>
					))
				)}
			</Container>
		</Anchor>
	),
)
